import { apiClient } from "@/services/apiClient";

export type CertificateProductPriceRow = {
  id: string;
  targetType: string;
  displayName: string;
  fixedAmountMinor: number;
  fixedCostItems: Array<{ label: string; amountMinor: number; includesIva: boolean }> | null;
  urUnits: string;
  urType: string;
  marginPercent: string;
  perFileStorageMinor: number;
  /** Physical delivery component (centésimos); subtracted for digital-only checkout. */
  deliveryFeeMinor: number;
  professionalStampMinor: number;
  /** Flat consumer service fee (centésimos, IVA-inclusive). 0 on catalog products. */
  consumerServiceFeeMinor: number;
  fixedAmountIncludesIva: boolean;
  urVariableIncludesIva: boolean;
  perFileStorageIncludesIva: boolean;
  deliveryFeeIncludesIva: boolean;
  marginIncludesIva: boolean;
  involvesAccountant: boolean;
  involvesNotary: boolean;
  currency: string;
  isActive: boolean;
  sortOrder: number;
  defaultBusinessDays: number;
  requiredDocumentTypes: string[] | null;
  fulfillmentModesAllowed: string[] | null;
  previewBaseAmountMinor: number | null;
  previewFinalAmountMinor: number | null;
  previewCurrency: string | null;
  description: string | null;
  updatedAt: string;
};

export type DgiUrSnapshotDto = {
  urValueMinor: number | null;
  referenceMonth: number;
  referenceYear: number;
  referenceLabel: string;
  source: string;
  parsedTableYear: number | null;
};

export type CajaNotarialUrSnapshotDto = {
  urValueMinor: number | null;
  arancelYear: number | null;
  semester: string | null;
  referenceLabel: string | null;
  referenceMonth: number | null;
  referenceYear: number | null;
  source: string;
};

export type UrSnapshotDto = {
  dgi: DgiUrSnapshotDto;
  cajaNotarial: CajaNotarialUrSnapshotDto;
};

export function listCertificateProductPrices(): Promise<CertificateProductPriceRow[]> {
  return apiClient
    .get<CertificateProductPriceRow[]>("/platform/certificate-pricing")
    .then((res) => res.data);
}

export function getCertificateProductPrice(id: string): Promise<CertificateProductPriceRow> {
  return apiClient
    .get<CertificateProductPriceRow>(`/platform/certificate-pricing/${encodeURIComponent(id)}`)
    .then((res) => res.data);
}

export function getAllowedCertificateTargetTypes(): Promise<string[]> {
  return apiClient
    .get<{ targetTypes: string[] }>("/platform/certificate-pricing/allowed-target-types")
    .then((res) => res.data.targetTypes);
}

export function getCertificateDocumentTypeOptions(): Promise<string[]> {
  return apiClient
    .get<{ documentTypes: string[] }>("/platform/certificate-pricing/document-type-options")
    .then((res) => res.data.documentTypes);
}

export function getUrSnapshot(): Promise<UrSnapshotDto> {
  return apiClient
    .get<UrSnapshotDto>("/platform/certificate-pricing/ur-snapshot")
    .then((res) => res.data);
}

export type UpsertCertificateProductPriceBody = {
  targetType: string;
  displayName: string;
  fixedAmountMinor: number;
  fixedCostItems?: Array<{ label: string; amountMinor: number; includesIva: boolean }> | null;
  urUnits: number;
  urType?: string;
  marginPercent?: number;
  perFileStorageMinor?: number;
  fixedAmountIncludesIva?: boolean;
  urVariableIncludesIva?: boolean;
  perFileStorageIncludesIva?: boolean;
  deliveryFeeIncludesIva?: boolean;
  marginIncludesIva?: boolean;
  involvesAccountant: boolean;
  involvesNotary: boolean;
  currency: string;
  isActive: boolean;
  sortOrder?: number;
  requiredDocumentTypes?: string[] | null;
  fulfillmentModesAllowed?: string[] | null;
  deliveryFeeMinor?: number;
  professionalStampMinor?: number;
  consumerServiceFeeMinor?: number;
  defaultBusinessDays?: number;
  description?: string | null;
};

export function upsertCertificateProductPrice(
  body: UpsertCertificateProductPriceBody
): Promise<CertificateProductPriceRow> {
  return apiClient
    .put<CertificateProductPriceRow>("/platform/certificate-pricing", body)
    .then((res) => res.data);
}
